import { css } from "@emotion/react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { SsoStatus } from "@rowan/prisma";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import type { UsersWithPermissions } from "../../../../api/src/types";
import { trpc } from "../../utils/trpc";
import { PrimaryButton, SecondaryButton } from "../bits/buttons";
import { Input } from "../bits/inputs";
import { FlexRowContainer } from "../bits/layout";
import { LoadingSpinner } from "../bits/loading";
import { Modal } from "../bits/Modal";

export type UserFormData = {
  name: string;
  email: string;
  organisationId?: number | null;
  permissions?: number[];
  admin: boolean;
  frjAccess: boolean;
  aiAccess: boolean;
  expiry: Dayjs;
  ssoStatus: SsoStatus;
};

const userSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().min(1, "Email is required").email("Invalid email format"),
});

type AddUserFormProps = {
  user?: UsersWithPermissions[number];
  onSubmit: (data: UserFormData) => void;
  error?: string;
};

export const AddUserForm = ({ onSubmit, error, user }: AddUserFormProps) => {
  const [formData, setFormData] = useState<UserFormData>({
    name: user?.name ?? "",
    email: user?.email ?? "",
    organisationId: user?.organisationId ?? null,
    admin: user?.admin ?? false,
    aiAccess: user?.aiAccess ?? false,
    frjAccess: user?.frjAccess ?? true,
    permissions: user?.permissions?.map((p) => p.publicationId),
    expiry: user?.expiry ? dayjs(user.expiry) : dayjs().add(1, "year"),
    ssoStatus: user?.ssoStatus ?? "all",
  });

  const [adminConfirmModal, setAdminConfirmModal] = useState(false);

  const { data: publications, isLoading } =
    trpc.adminGetPublications.useQuery();
  const { data: organisations, isLoading: orgsIsLoading } =
    trpc.adminGetOrganisations.useQuery();

  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const theme = useTheme();

  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handlePermissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const permissionId = Number(e.target.name);

    if (formData.permissions?.includes(permissionId)) {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions?.filter((p) => p !== permissionId),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: [...(prev.permissions ?? []), permissionId],
      }));
    }
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const result = userSchema.safeParse(formData);
    if (!result.success) {
      setErrors(
        result.error.errors.reduce(
          (acc, error) => {
            const field = error.path[0] as keyof typeof errors;
            acc[field] = error.message;
            return acc;
          },
          {} as { name?: string; email?: string },
        ),
      );
      return;
    }
    if (
      (formData.admin && user?.admin !== true) ||
      (!formData.admin && user?.admin === true)
    ) {
      setAdminConfirmModal(true);
    } else {
      onSubmit(formData);
    }
  };

  if (isLoading || orgsIsLoading) {
    return <LoadingSpinner />;
  }
  return (
    <>
      <Modal
        key="createAdminConfirm"
        isOpen={adminConfirmModal}
        onClose={() => {
          setAdminConfirmModal(false);
        }}
        renderButton={() => null}
        header={
          <h2>{formData.admin ? "Create admin user" : "Remove admin user"}</h2>
        }
        content={
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
              padding: 4px;
            `}
          >
            {formData.admin
              ? `Are you sure you want to make user ${formData.name} an admin?`
              : `Are you sure you want to remove admin rights from user ${formData.name}?`}
          </div>
        }
        footer={
          <FlexRowContainer style={{ justifyContent: "end", gap: "8px" }}>
            <SecondaryButton
              onClick={() => {
                setAdminConfirmModal(false);
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                setAdminConfirmModal(false);
                onSubmit(formData);
              }}
            >
              Confirm
            </PrimaryButton>
          </FlexRowContainer>
        }
      />
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Input
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          required
          disabled={!!user}
        />
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          required
        />
        {organisations && organisations.length > 0 && (
          <FormControl fullWidth>
            <InputLabel id="organisation-select-label">Organisation</InputLabel>
            <Select
              labelId="organisation-select-label"
              id="organisation-select"
              value={formData.organisationId ?? ""}
              label="Organisation"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  organisationId:
                    e.target.value === "" ? null : Number(e.target.value),
                })
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {organisations
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
        {!formData.organisationId && (
          <FormControl fullWidth>
            <InputLabel id="sso-status-select-label">SSO Status</InputLabel>
            <Select
              labelId="sso-status-select-label"
              id="sso-status-select"
              value={formData.ssoStatus ?? ""}
              label="SSO Status"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ssoStatus: e.target.value as SsoStatus,
                })
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="password_only">Password Only</MenuItem>
              <MenuItem value="sso_only">SSO Only</MenuItem>
              <MenuItem value="microsoft_only">Microsoft Only</MenuItem>
              <MenuItem value="google_only">Google Only</MenuItem>
            </Select>
          </FormControl>
        )}
        {!formData.organisationId &&
          !formData.admin &&
          publications &&
          publications.length > 0 && (
            <FormGroup>
              <FormLabel>Book Access</FormLabel>
              {publications.map((publication) => (
                <FormControlLabel
                  key={publication.id}
                  control={
                    <Checkbox
                      onChange={handlePermissionChange}
                      name={String(publication.id)}
                      disabled={publication.status !== "PUBLISHED"}
                      css={css`
                        padding: 0 9px;
                      `}
                      checked={
                        !!formData.permissions?.some(
                          (p) => p === publication.id,
                        )
                      }
                    />
                  }
                  label={
                    publication.title +
                    (publication.status !== "PUBLISHED"
                      ? " (" + publication.status + ")"
                      : "")
                  }
                />
              ))}
            </FormGroup>
          )}
        {!formData.organisationId && !formData.admin && (
          <FormControlLabel
            label="FRJ Access"
            control={
              <Checkbox
                name="frjAccess"
                checked={formData.frjAccess}
                onChange={(e) =>
                  setFormData({ ...formData, frjAccess: e.target.checked })
                }
              />
            }
            style={{ color: theme.palette.text.primary }}
          />
        )}
        <FormControlLabel
          label="AI Access"
          control={
            <Checkbox
              name="aiAccess"
              checked={formData.aiAccess}
              onChange={(e) =>
                setFormData({ ...formData, aiAccess: e.target.checked })
              }
            />
          }
          style={{ color: theme.palette.text.primary }}
        />
        {!formData.organisationId && (
          <DatePicker
            label="Expiry date"
            value={formData.expiry}
            onChange={(value) =>
              setFormData({
                ...formData,
                expiry: dayjs(value),
              })
            }
          />
        )}
        <FormControlLabel
          label="Is Admin"
          control={
            <Checkbox
              name="admin"
              checked={formData.admin}
              onChange={(e) =>
                setFormData({ ...formData, admin: e.target.checked })
              }
            />
          }
          style={{ color: theme.palette.text.primary }}
        />
        <PrimaryButton type="submit">
          {user ? "Update User" : "Add User"}
        </PrimaryButton>
        <p ref={errorRef} style={{ color: theme.palette.error.main }}>
          {error}
        </p>
      </Box>
    </>
  );
};
